import React, { Fragment, useRef } from 'react';
import { Form } from 'redux-form';
import cn from 'classnames';
import omit from 'lodash/omit';
import get from 'lodash/get';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { DisabledInputShareLink } from 'chameleon/ui-stack/utilities/share-link';
import DataWrapper from './wrappers/DataWrapper';
import Context from './Context';
import CommonOptions from './options/CommonOptions';
import TimeOptions from './options/TimeOptions';
import AdvancedOptions from './options/AdvancedOptions';
import Footer from './options/Footer';
import { assignAdminCoordAlert } from 'legacy-src/components/generic/messages';
import ResourceTitles from './ResourceTitles';
import useSppTelemetrySend from 'chameleon/ui-stack/utilities/use-spp-telemetry-send';
import { TELEMETRY_EVENTS } from 'legacy-src/utility/telemetry';

import './styles.scss';

/**
 * This component serves only to gather data from it's high order components and display the UI.
 * It has been added to Chameleon App in https://academicmerit.atlassian.net/browse/FY-19867.
 *
 * NOTE! Please keep it only to server UI information to the browser.
 * If you need to process any data you can check the HOCs used by this component.
 * */
const AssignManageForm = (props) => {
  const {
    isAssign,
    isLoading,
    showTimeSettings,
    submit,
    formData,
    withAdvancedSettings,
    handleAssign,
    formComponentRef,
    shareLinkData,
    isAdminOrCoordinator,
    isResourceForm,
    isPPC,
    items,
    itemTitle,
    learnosityReference,
    onResourceDeselect,
    subject,
    itemId,
    isSPP,
    disablePpcAutoAssignFlag,
    assignment,
    eventFromSPPPlayer = false,
    spacingAroundContent,
    isFormatPrint,
    isCasAssignmentFF,
  } = props;
  const { printToggle } = formData;
  const manageSettingsDetailsRef = useRef(null);

  const sppTelemetrySend = useSppTelemetrySend({
    subject,
  });

  const sendTelemetry = () => {
    if (isSPP) {
      const telemetryData = {
        assigned: isAssign ? 1 : 0,
        assigned_date: new Date(),
        assigned_start: formData.startsAt && formData.startsAt.toDate(),
        assigned_due: formData.dueAt && formData.dueAt.toDate(),
        spp_id: itemId,
        spp_learnosity_reference:
          learnosityReference ||
          get(assignment, 'assessment.learnosity_reference'),
        spp_title: itemTitle || get(assignment, 'title'),
        event_type: isAssign
          ? TELEMETRY_EVENTS.teacherAssign
          : TELEMETRY_EVENTS.teacherUpdate,
        assigned_from_spp_player: eventFromSPPPlayer,
      };
      if (!isAssign) {
        telemetryData.assignment_id = assignment.id;
      }
      sppTelemetrySend(telemetryData);
    }
  };

  const updateSettingsModalCASInfoText = (
    <span className="my-4.2 px-12.2 pt-8.4 pb-4.2 text-[14px] font-medium">
      *Update settings for specific students in the student table on the
      Assignment Overview page.{' '}
      <a
        href="https://apclassroom.clickhelp.co/articles/#!ap-classroom-user-guide-for-teachers-publication/tch-updating-assignment-settings"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more.
      </a>
    </span>
  );

  return (
    <Context.Provider
      value={{
        ...omit(props, ['formData', 'formComponentRef', 'shareLinkData']),
        ...formData,
        manageSettingsDetailsRef,
        hasShareLink: !!shareLinkData,
        sendTelemetry,
      }}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Fragment>
          {isAdminOrCoordinator &&
            assignAdminCoordAlert(
              isResourceForm,
              isPPC,
              disablePpcAutoAssignFlag,
            )}
          <Form
            autoComplete="off"
            className="ft-assign-manage-form"
            ref={formComponentRef}
            onSubmit={isAssign ? handleAssign : () => {}}
          >
            <input type="hidden" value="disable-autocomplete" />
            <input type="hidden" name="assignees" />
            <input type="hidden" name="update_assignees" />
            <input type="hidden" name="is_final_exam" value={false} />
            <input type="hidden" name="final_exam_sessions" value={false} />
            <div className="max-h-70-screen overflow-auto border-b border-gray-300">
              <div
                className={cn(
                  'ft-assign-manage-form-container mb-4.2',
                  spacingAroundContent,
                )}
              >
                {isResourceForm && (items || []).length > 1 && (
                  <ResourceTitles
                    resources={items}
                    onRemove={onResourceDeselect}
                  />
                )}
                <div
                  className={cn('ft-amf-default-container', {
                    'single-column': printToggle,
                  })}
                >
                  <CommonOptions />
                  {showTimeSettings && <TimeOptions />}
                </div>
              </div>
              {isCasAssignmentFF &&
                !isAssign &&
                !printToggle &&
                updateSettingsModalCASInfoText}
              {withAdvancedSettings && (
                <AdvancedOptions
                  spacingAroundContent={spacingAroundContent}
                  isFormatPrint={isFormatPrint}
                />
              )}
              {!isResourceForm && shareLinkData && (
                <DisabledInputShareLink
                  className="my-4.2 px-12.2 pt-8.4 pb-4.2 border-t border-gray-300"
                  header={shareLinkData.header}
                  link={shareLinkData.link}
                  timeout={shareLinkData.timeout}
                />
              )}
            </div>
            <Footer submit={submit} submitBtnText="Assign" />
          </Form>
        </Fragment>
      )}
    </Context.Provider>
  );
};

AssignManageForm.displayName = 'AssignManageForm';

export default DataWrapper(AssignManageForm);
